import { useEffect, useRef, useState } from "react";
import {
  Menu,
  ChevronLeft,
  User,
  Bell,
  Search,
  X,
  Tag,
  Home,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import MobileAccordionMenu from "./MobileAccordionMenu";
import CategoryScroll from "./CategoryScroll";
import apiClient from "@/lib/apiClient";
import userImage from "@/assets/img/user.png";
import useAuth from "@/auth/useAuth";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};
export default function Header({ toggleSidebar }) {
  const { currentUser, getUserImage } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState({});
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isShowLodMore, setIsShowLodMore] = useState(false);
  const [nextPage, setNextPage] = useState(2);
  const [exploreContent, setExploreContent] = useState([]);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);

  const query = useQuery();
  const tag = query.get("tag");

  useEffect(() => {
    setIsLoading(true);
    fetchData({ tag: tag });
  }, [tag]);

  const fetchData = async ({ page = 1, tag }) => {
    try {
      const { data, ok } = await apiClient.get(
        `/content/explore?page=${page}&tag=${tag}`
      );

      if (!ok) {
        return toast.error("مشکلی به وجود امده است");
      }

      setIsShowLodMore(data.data.hasNextPage);
      setNextPage(data.data.nextPage);

      setExploreContent(data.data.docs);
    } catch (error) {
      toast.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchNextPageData = async ({ page = 1 }) => {
    try {
      const { data, ok } = await apiClient.get(`/content/explore?page=${page}`);

      if (!ok) {
        return toast.error("مشکلی به وجود امده است");
      }

      setIsShowLodMore(data.data.hasNextPage);
      setNextPage(data.data.nextPage);

      setExploreContent((prevContent) => [...prevContent, ...data.data.docs]);
    } catch (error) {
      toast.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input changes with debounce
  useEffect(() => {
    if (searchTerm.length > 0) {
      setIsSearching(true);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        search();

        setShowSearchResults(true);
        setIsSearching(false);
      }, 500); // 500ms debounce
    } else {
      setShowSearchResults(false);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedCategory]);

  const search = async () => {
    setIsSearching(true);
    const { data, ok } = await apiClient.get(
      `/content/search?q=${searchTerm}&type=${selectedCategory}`
    );
    setShowSearchResults(true);
    setIsSearching(false);

    if (!ok) {
      return toast.error("مشکلی درگرفتن اطلاعات به وجود امده است");
    }
    setSearchResults(data);
  };

  // Handle clicks outside search results to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if search results are empty
  const hasResults = () => {
    if (!searchResults) return false;

    return (
      (searchResults.users && searchResults.users.length > 0) ||
      (searchResults.tags && searchResults.tags.length > 0) ||
      (searchResults.villages && searchResults.villages.length > 0)
    );
  };

  const [searchOpen, setSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);

  const toggleSearch = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setSearchOpen(!searchOpen);
  };

  // Handle clicks outside the search container to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchOpen &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    // Focus the input when search is opened
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  return (
    <header className="bg-white shadow-md sticky top-0 z-30 font-vazir">
      {/* Top Header */}
      <div className="container-custom py-2">
        <div className="flex items-center justify-between">
          {/* Logo and Menu Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-1 rounded-full hover:bg-gray-100"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/profile" className="flex items-center gap-2">
              <img
                src={getUserImage(currentUser?.image)}
                alt="روستا تی وی"
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
              />
              <h1 className="text-sm sm:text-lg font-bold">
                {currentUser?.fullName || "روستا تی وی"}
              </h1>
            </Link>
          </div>

          {/* Search Bar - Only visible when expanded */}
          {searchOpen && (
            <div
              ref={searchContainerRef}
              className="absolute top-0 left-0 right-0 bg-white p-2 z-40 shadow-md"
            >
              <div className="flex items-center relative flex-1 max-w-full md:max-w-md mx-4">
                <div className="relative flex-1">
                  {isLoading ? (
                    <div className="h-10 w-full shimmer rounded-lg"></div>
                  ) : (
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="جستجو..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full py-2 px-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        {isSearching ? (
                          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                        ) : (
                          <Search className="h-5 w-5" />
                        )}
                      </div>

                      {/* Search Results Dropdown */}
                      {showSearchResults && searchTerm.length > 0 && (
                        <div
                          ref={searchResultsRef}
                          className="absolute top-full right-0 left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 max-h-80 overflow-y-auto"
                        >
                          {isSearching ? (
                            <div className="p-4 text-center">
                              <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin mx-auto"></div>
                              <p className="text-sm text-gray-500 mt-2">
                                در حال جستجو...
                              </p>
                            </div>
                          ) : hasResults() ? (
                            <>
                              {/* Users Results */}
                              {searchResults.users &&
                                searchResults.users.length > 0 && (
                                  <div className="p-2 border-b border-gray-100">
                                    <h3 className="text-xs font-bold text-gray-500 mb-2 pr-2">
                                      کاربران
                                    </h3>
                                    {searchResults.users.map((user) => (
                                      <Link
                                        to={"/user/" + user._id}
                                        key={user.id}
                                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                                      >
                                        <img
                                          src={
                                            user.image
                                              ? `${
                                                  import.meta.env
                                                    .VITE_WEBSITE_URL
                                                }/uploads/user/${user.image}`
                                              : userImage
                                          }
                                          alt={user.fullName}
                                          className="w-8 h-8 rounded-full object-cover"
                                        />
                                        <div className="flex-1">
                                          <p className="text-sm font-medium">
                                            {user.fullName}
                                          </p>
                                          {/* <p className="text-xs text-gray-500">
                                      {user.followers} دنبال‌کننده
                                    </p> */}
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                )}

                              {/* Villages Results */}
                              {searchResults.villages &&
                                searchResults.villages.length > 0 && (
                                  <div className="p-2">
                                    <h3 className="text-xs font-bold text-gray-500 mb-2 pr-2">
                                      روستاها
                                    </h3>
                                    {searchResults.villages.map((village) => (
                                      <Link
                                        key={village._id}
                                        to={`/wiki/village/${village._id}`}
                                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                                      >
                                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                          <Home className="w-4 h-4 text-primary" />
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium">
                                            {village.name}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {village.province} • {village.posts}{" "}
                                            پست
                                          </p>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                            </>
                          ) : (
                            <div className="p-4 text-center">
                              <p className="text-sm text-gray-500">
                                نتیجه‌ای یافت نشد
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>{" "}
                <button
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  onClick={toggleSearch}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* User Actions */}
          <div className="flex items-center gap-2">
            <button
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={toggleSearch}
            >
              <Search className="h-5 w-5" />
            </button>
            <button className="p-1 rounded-full hover:bg-gray-100">
              <Bell className="h-5 w-5" />
            </button>
            <Link
              to={"/profile"}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <User className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Search overlay for mobile */}
      {searchOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30"
          onClick={toggleSearch}
        ></div>
      )}

      {/* Category Scroll */}
      {location.pathname === "/" && <CategoryScroll />}
    </header>
  );
}
